// src/lib/schema/components/OrganizationSchema.tsx

import React from 'react';
import { generateOrganizationSchema, schemaToString } from '@/lib/schema';

/**
 * Interface for organization address
 */
interface OrganizationAddress {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

/**
 * Interface for social media links
 */
interface SocialMedia {
  facebookPageURL?: string;
  instagramPageURL?: string;
  twitterPageURL?: string;
  pinterestPageURL?: string;
}

/**
 * Interface for opening hours
 */
interface OpeningHours {
  days: string | string[];
  opens: string;
  closes: string;
}

/**
 * Interface for organization options
 */
interface OrganizationOptions {
  baseURL?: string;
  googleMapsPlaceID?: string;
  name?: string;
  address?: OrganizationAddress;
  phone?: string;
  email?: string;
  logo?: string;
  socialMedia?: SocialMedia;
  defaultFacebookURL?: string;
  defaultInstagramURL?: string;
  openingHours?: OpeningHours[];
  [key: string]: any;
}

/**
 * Props for the OrganizationSchema component
 */
interface OrganizationSchemaProps {
  options?: OrganizationOptions;
}

/**
 * Component that renders the JSON-LD schema for the organization
 */
export function OrganizationSchema({ options = {} }: OrganizationSchemaProps): React.ReactElement | null {
  // Generate the organization schema
  const organizationSchema = generateOrganizationSchema(options);

  // Return null if schema generation failed
  if (!organizationSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(organizationSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}